import type {
  AuditContextRequest,
  CommunicationPurpose,
  MediumType,
  TargetAudience,
} from "@/types";

export interface AuditContextFormValues {
  mediumType: MediumType | "";
  targetAudience: TargetAudience | "";
  /**
   * Declared communication purpose (scope gate, byh4n). Empty string => undeclared
   * => the model decides (ambiguity-default: analyze). A prior, not an override.
   */
  communicationPurpose: CommunicationPurpose | "";
  industrySector: string;
  geographicScope: string;
  publicationDate: string;
}

/**
 * Convert form values to API request format.
 * Returns undefined when required fields are not complete.
 *
 * When `communicationPurpose` is "educational_csr", the form deliberately hides
 * and clears the Q2 targetAudience picker; in that case targetAudience defaults
 * to "general_consumers" so the declared CSR context is fully formed on the wire.
 */
export function toContextRequest(
  values: AuditContextFormValues,
): AuditContextRequest | undefined {
  if (!values.mediumType) {
    return undefined;
  }

  const targetAudience =
    values.targetAudience ||
    (values.communicationPurpose === "educational_csr"
      ? "general_consumers"
      : undefined);

  if (!targetAudience) {
    return undefined;
  }

  return {
    mediumType: values.mediumType,
    targetAudience,
    communicationPurpose: values.communicationPurpose || undefined,
    industrySector: values.industrySector || undefined,
    geographicScope: values.geographicScope || undefined,
    publicationDate: values.publicationDate || undefined,
  };
}

/**
 * Get default form values.
 * Defaults to website + general consumers for full UCPD analysis.
 *
 * `defaultMarket` seeds `geographicScope` from the user's per-user default
 * (see `getStoredDefaultMarket`); it falls back to Polska so anonymous / first
 * load behaves exactly as before. The market is a silent inherited default that
 * the user can override per-run.
 * `industrySector` represents the explicit per-audit product/claim domain declaration.
 * Company profile `industry_sector` must not auto-seed this field; it defaults to empty ("").
 */
export function getDefaultContextValues(
  defaultMarket = "Polska",
): AuditContextFormValues {
  const today = new Date().toLocaleDateString("sv-SE");

  return {
    mediumType: "website",
    targetAudience: "general_consumers",
    communicationPurpose: "",
    industrySector: "",
    geographicScope: defaultMarket,
    publicationDate: today,
  };
}
